// Error message mappings for user-friendly display
const errorMessages: Record<string, string> = {
  // Authentication errors
  'Invalid credentials': 'Número de teléfono o contraseña incorrectos',
  'User already exists with this phone number': 'Ya existe una cuenta con este número de teléfono',
  'Invalid or expired reset token': 'El enlace ha expirado o no es válido',
  'Reset token has expired': 'El enlace ha expirado. Por favor, solicita uno nuevo',
  'Reset token has already been used': 'Este enlace ya ha sido utilizado',
  'Enlace inválido o expirado': 'El enlace no es válido o ha expirado',
  'El enlace ha expirado': 'El enlace ha expirado. Por favor, solicita uno nuevo',
  'Este enlace ya ha sido utilizado': 'Este enlace ya ha sido utilizado',

  // Tournament registration errors
  'Player already registered': 'Este jugador ya está registrado en el torneo',
  'Player is already registered for this tournament': 'Ya estás registrado en este torneo',
  'Tournament is locked': 'El torneo está cerrado para nuevas inscripciones',
  'Tournament is full': 'El torneo ha alcanzado el límite de jugadores',
  'Partner already registered with another player': 'Tu pareja ya está registrada con otro jugador',
  'You are already registered with a different partner': 'Ya estás registrado con una pareja diferente',
  'Cannot register the same player as both player and partner': 'No puedes registrarte contigo mismo como pareja',

  // Profile errors
  'Value is too large': 'El archivo es demasiado grande. Por favor, selecciona uno más pequeño',

  // Network errors
  'NetworkError': 'Error de conexión. Por favor, verifica tu internet',
  'Failed to fetch': 'No se pudo conectar con el servidor',

  // Generic errors
  'Unauthorized': 'No tienes permisos para realizar esta acción',
  'Not found': 'No se encontró el recurso solicitado',
  'Server error': 'Error del servidor. Por favor, intenta de nuevo más tarde',
};

// Check if we're in development mode
const isDevelopment = process.env.NODE_ENV === 'development';

// Check if debug mode is enabled (can be toggled via localStorage)
const isDebugMode = (): boolean => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('debugMode') === 'true';
  }
  return false;
};

// Enable/disable debug mode
export const toggleDebugMode = (enabled: boolean) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('debugMode', enabled.toString());
  }
};

export interface ProcessedError {
  userMessage: string;
  technicalMessage?: string;
  showTechnical: boolean;
}

/**
 * Process an error and return user-friendly message
 */
export const processError = (error: any): ProcessedError => {
  let technicalMessage = '';
  let userMessage = 'Ha ocurrido un error inesperado';

  // Extract the error message
  if (error instanceof Error) {
    technicalMessage = error.message;
  } else if (typeof error === 'string') {
    technicalMessage = error;
  } else if (error?.message) {
    technicalMessage = error.message;
  } else {
    technicalMessage = JSON.stringify(error);
  }

  // Check for matching user-friendly message
  for (const [key, friendlyMessage] of Object.entries(errorMessages)) {
    if (technicalMessage.toLowerCase().includes(key.toLowerCase())) {
      userMessage = friendlyMessage;
      break;
    }
  }

  // Check for specific patterns
  if (technicalMessage.includes('Value is too large') && technicalMessage.includes('MiB')) {
    const match = technicalMessage.match(/(\d+\.?\d*)\s*MiB/);
    if (match) {
      userMessage = `El archivo es demasiado grande (${match[1]} MB). El límite es 1 MB.`;
    }
  }

  return {
    userMessage,
    technicalMessage: isDevelopment || isDebugMode() ? technicalMessage : undefined,
    showTechnical: isDevelopment || isDebugMode(),
  };
};

/**
 * Format error for display in UI components
 */
export const formatError = (error: any): string => {
  const processed = processError(error);

  if (processed.showTechnical && processed.technicalMessage) {
    return `${processed.userMessage}\n\n[Debug] ${processed.technicalMessage}`;
  }

  return processed.userMessage;
};

/**
 * Log error for debugging (only in development or debug mode)
 */
export const logError = (context: string, error: any): void => {
  if (isDevelopment || isDebugMode()) {
    console.error(`[${context}]`, error);
  }
};